<template>
    <div class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]">
        <div class=" text-[#414141] font-sans">
            <h3 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">New Project Expenses</h3>
            <p class="text-[14px] leading-[22px] mb-[0.5rem]">For each project, choose the categories of needs required, such as staffing, technology, facilities, equipment, supplies, or travel. Estimate the total cost of each project, up to over the next three (3) years. Use only well-researched and realistic figures based on quotes, past expenses, or market rates.</p>

            <div v-if="isLoading" class="flex justify-center items-center h-[200px]">
                <ActivityIndicator />
            </div>

            <!-- Project Tabs -->
            <div v-else class="mb-6">
                <div class="flex space-x-4 border-b border-gray-200">
                    <button
                        v-for="(project, index) in projects"
                        :key="project.id || index"
                        @click="handleProjectChange(index)"
                        :class="[
                            'px-4 py-2 text-sm font-medium border-b-2 -mb-px',
                            activeProjectIndex === index
                                ? 'border-[#00423D] text-[#00423D]'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        ]"
                    >
                        {{ project.project_name || `Project ${index + 1}` }}
                    </button>
                </div>
            </div>

            <!-- Budget Table for Active Project -->
            <div v-if="projects.length > 0">
                <div class="mb-4">
                    <h4 class="font-['Open_Sans'] font-semibold text-[18px] leading-[140%] text-[#414141] align-middle">{{ projects[activeProjectIndex].project_name }}</h4>
                </div>
                <BudgetTable 
                    :categories="projectCategories[activeProjectIndex]"
                    :total-programs-budget="total_projects_budget"
                    @delete-item="handleDeleteItem"
                    @data-changed="handleDataChanged"
                />
            </div>
            <div v-else class="text-center py-8 text-gray-500">
                Please add projects in the New Projects section first.
            </div>
            <div v-if="validationError" ref="errorMessageRef" class="mt-4 text-red-500 text-sm">
                {{ validationError }}
            </div>
        </div>
    </div>

    <!-- Confirmation Popup -->
    <div v-if="conformationPopupIsOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 class="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p class="text-gray-600 mb-6">Are you sure you want to delete this item?</p>
            <div class="flex justify-end space-x-4">
                <button
                    @click="onCancelRemove"
                    class="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                    Cancel
                </button>
                <button
                    @click="onConformRemove"
                    class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                    Delete
                </button>
            </div>
        </div>
    </div>

    <!-- Incomplete Project Popup -->
    <div v-if="incompleteProjectPopupIsOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 class="text-lg font-semibold mb-4 text-[#00423D]">Incomplete Project Expenses</h3>
            <p class="text-gray-600 mb-6">{{ incompleteProjectMessage }}</p>
            <div class="flex justify-end space-x-4">
                <button
                    @click="onCancelIncompleteProject"
                    class="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                    Cancel
                </button>
                <button
                    @click="onGoToIncompleteProject"
                    class="px-4 py-2 bg-[#00423D] text-white rounded-lg hover:bg-[#003632]"
                >
                    Go to Project
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useStepValidation } from '@/composables/useStepValidation';
import BudgetTable from '@/components/BudgetTable.vue';
import ActivityIndicator from '@/components/ActivityIndicator.vue';
import { useAuthStore } from '@/stores/auth';
import { useRoutesManagement } from '@/stores/routesManagement';
import { useFetchCache } from '@/composables/fetchCache';
import { useFetch } from '@/composables/fetch';
import { usePostAnswer } from '@/composables/postAnswer';
import global from "@/global";

const router = useRouter();
const authStore = useAuthStore();
const routesManagementStore = useRoutesManagement();

// Use the validation composable with default settings
useStepValidation();

const isLoading = ref(true);
const total_projects_budget = ref(0);
const conformationPopupIsOpen = ref(false);
const deletionFunction = ref(null);
const activeProjectIndex = ref(0);
const projects = ref([]);
const projectCategories = ref([]);
const validationError = ref('');
const isSaving = ref(false);
const errorMessageRef = ref(null);

// Add incomplete project popup variables
const incompleteProjectPopupIsOpen = ref(false);
const incompleteProjectMessage = ref('');
const incompleteProjectIndex = ref(null);

// Add auto-save debounce timer
let autoSaveTimer = null;
const AUTO_SAVE_DELAY = 2000; // 2 seconds delay

// Add computed properties for tab navigation
const tabs = computed(() => {
  return projects.value.map((project, index) => ({
    id: index,
    name: project.project_name || `Project ${index + 1}`,
    isValid: () => validateCurrentProject(index)
  }));
});

const activeTab = computed({
  get: () => activeProjectIndex.value,
  set: (value) => {
    activeProjectIndex.value = value;
  }
});

const EXPENSE_TYPES = [
  'Personnel and Benefits',
  'Professional and Contractual Fees',
  'Printing and Publications',
  'Travel, Conferences, and Meetings',
  'Marketing and Communications',
  'Facilities',
  'Other Expenses',
  'In-Kind Goods & Services'
];

// Add this mapping at the top, after EXPENSE_TYPES
const API_CATEGORY_KEY_MAP = {
  'Personnel and Benefits': 'personnel_and_benefits',
  'Professional and Contractual Fees': 'professional_and_contractual_fees',
  'Printing and Publications': 'printing_and_publications',
  'Travel, Conferences, and Meetings': 'travel_conferences_and_meetings',
  'Marketing and Communications': 'marketing_and_communications',
  'Facilities': 'facilities',
  'Other Expenses': 'other_expenses',
  'In-Kind Goods & Services': 'in-kind_goods_services',
};

// API Integration using composables
const { error: fetchError, fetchCachedData, cachedData } = useFetchCache({
  uri: "/questions/new_projects_expenses",
  headers: {
    Authorization: `Bearer ${authStore.user.accessToken}`,
  },
});

const expensesFetch = useFetch({
  uri: "/expenses",
  headers: {
    Authorization: `Bearer ${authStore.user.accessToken}`,
  },
});

const deleteExpenseFetch = useFetch({
  uri: "/expenses",
  headers: {
    Authorization: `Bearer ${authStore.user.accessToken}`,
  },
  method: 'DELETE',
});

const answerPost = usePostAnswer({
  uri: `/expenses`,
  headers: {
    Authorization: `Bearer ${authStore.user.accessToken}`,
    "Content-Type": "application/json",
  },
});

// Initialize data
onMounted(async () => {
  try {
    // Fetch question data
    await fetchCachedData();
    localStorage.setItem(
      "questionFetchData",
      JSON.stringify(cachedData.value.question)
    );

    // Fetch projects
    const projectsResponse = await fetch(`${global.API_URL}/projects`, {
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });
    const projectsData = await projectsResponse.json();
    projects.value = projectsData.projects || [];

    // Initialize categories for each project
    projectCategories.value = projects.value.map(() => 
      EXPENSE_TYPES.map(type => ({
        name: type,
        isOpen: type === 'Personnel and Benefits',
        items: []
      }))
    );

    // Fetch expenses for each project
    await Promise.all(projects.value.map(async (project, index) => {
      const response = await fetch(`${global.API_URL}/expenses?project_id=${project.id}`, {
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`,
        },
      });
      const data = await response.json();
      
      // Map expenses to categories for this project
      projectCategories.value[index] = EXPENSE_TYPES.map(type => {
        const categoryKey = API_CATEGORY_KEY_MAP[type];
        const categoryExpenses = data.expenses[categoryKey] || [];
        return {
          name: type,
          isOpen: type === 'Personnel and Benefits',
          items: categoryExpenses.map(expense => ({
            id: expense.id,
            name: expense.expense_description,
            year1: Number(expense.expense_cost),
            year2: Number(expense.expense_cost_year_2),
            year3: Number(expense.expense_cost_year_3)
          }))
        };
      });
    }));

    // Calculate total projects budget
    if (expensesFetch.fetchedData.value.projects && expensesFetch.fetchedData.value.projects.length > 0) {
      total_projects_budget.value = Number(expensesFetch.fetchedData.value.projects[0].expense_cost);
    }

    isLoading.value = false;
  } catch (error) {
    console.error('Error loading data:', error);
    isLoading.value = false;
  }
});

// Cleanup on unmount
onUnmounted(() => {
  if (autoSaveTimer) {
    clearTimeout(autoSaveTimer);
  }
});

// Handle project change
const handleProjectChange = (index) => {
  activeProjectIndex.value = index;
};

// Validate current project
const validateCurrentProject = (projectIndex) => {
  if (projectIndex >= projectCategories.value.length) {
    return false;
  }
  
  const projectCategories = projectCategories.value[projectIndex];
  return projectCategories.some(category => category.items.length > 0);
};

// Function to scroll to error message
const scrollToError = () => {
  if (errorMessageRef.value) {
    errorMessageRef.value.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'center' 
    });
  }
};

// Validate current tab (for parent component)
const validateCurrentTab = async () => {
  try {
    // Clear previous validation error
    validationError.value = '';
    
    // First check if the current project has expenses
    const currentProjectCategories = projectCategories.value[activeProjectIndex.value];
    const currentProjectHasExpenses = currentProjectCategories.some(category => category.items.length > 0);
    
    if (!currentProjectHasExpenses) {
      // Current project is incomplete, stay on this tab and show error
      validationError.value = `Please add at least one expense item for ${projects.value[activeProjectIndex.value].project_name || `Project ${activeProjectIndex.value + 1}`} before proceeding.`;
      await nextTick();
      scrollToError();
      return false;
    }
    
    // Check if this is the last project tab
    const isLastProject = activeProjectIndex.value === projects.value.length - 1;
    
    if (isLastProject) {
      // Only check all projects when on the last project tab
      const incompleteProjects = projectCategories.value.map((projectCategories, index) => ({
        index,
        hasExpenses: projectCategories.some(category => category.items.length > 0)
      })).filter(project => !project.hasExpenses);

      if (incompleteProjects.length > 0) {
        // Show popup for the first incomplete project
        const firstIncompleteProject = incompleteProjects[0];
        incompleteProjectIndex.value = firstIncompleteProject.index;
        incompleteProjectMessage.value = `You haven't completed the expense table for ${projects.value[firstIncompleteProject.index].project_name || `Project ${firstIncompleteProject.index + 1}`}. Please go to this project tab and complete it before proceeding.`;
        incompleteProjectPopupIsOpen.value = true;
        return false;
      }
      
      // All projects are complete, try to save the expenses
      try {
        await saveExpenses();
        return true;
      } catch (error) {
        console.error('Error saving expenses:', error);
        validationError.value = "Failed to save expenses. Please try again.";
        await nextTick();
        scrollToError();
        return false;
      }
    } else {
      // Not the last project, just save current project's expenses and allow proceeding
      try {
        await saveExpenses();
        return true;
      } catch (error) {
        console.error('Error saving expenses:', error);
        validationError.value = "Failed to save expenses. Please try again.";
        await nextTick();
        scrollToError();
        return false;
      }
    }
  } catch (error) {
    console.error('Error validating current tab:', error);
    validationError.value = error.message || 'Failed to validate data. Please try again.';
    await nextTick();
    scrollToError();
    return false;
  }
};

// Delete confirmation popup handlers
function onCancelRemove() {
  conformationPopupIsOpen.value = false;
}

async function onConformRemove() {
  conformationPopupIsOpen.value = false;
  await deletionFunction.value();
  deletionFunction.value = null;
}

// Incomplete project popup handlers
function onCancelIncompleteProject() {
  incompleteProjectPopupIsOpen.value = false;
  incompleteProjectMessage.value = '';
  incompleteProjectIndex.value = null;
}

function onGoToIncompleteProject() {
  if (incompleteProjectIndex.value !== null) {
    activeProjectIndex.value = incompleteProjectIndex.value;
  }
  incompleteProjectPopupIsOpen.value = false;
  incompleteProjectMessage.value = '';
  incompleteProjectIndex.value = null;
}

// Delete expense
const deleteExpense = async (expenseId) => {
  try {
    const response = await fetch(`${global.API_URL}/expenses/${expenseId}`, {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });
    const data = await response.json();
    return data?.status || false;
  } catch (error) {
    console.error('Error deleting expense:', error);
    return false;
  }
};

// Save expenses data
const saveExpenses = async () => {
  if (isSaving.value) {
    return;
  }

  try {
    isSaving.value = true;
    
    // Filter out empty items (no name or all costs are 0)
    const allExpensesToSave = projectCategories.value.flatMap((projectCategories, projectIndex) => 
      projectCategories.flatMap(category => 
        category.items
          .filter(item => item.name && item.name.trim() !== '') // Only save items with names
          .map(item => ({
            id: item.id,
            expense_type: category.name,
            expense_description: item.name.trim(),
            expense_cost: item.year1.toString().replace(/,/g, ""),
            expense_cost_year_2: item.year2.toString().replace(/,/g, ""),
            expense_cost_year_3: item.year3.toString().replace(/,/g, ""),
            expenseable_type: "Project",
            expenseable_id: projects.value[projectIndex].id
          }))
      )
    );
    
    // Don't save if no expenses to save
    if (allExpensesToSave.length === 0) {
      return { status: true, expenses: {} };
    }
    
    // Make API call to save all expenses
    const response = await fetch(`${global.API_URL}/expenses`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
      body: JSON.stringify({
        expenses: allExpensesToSave,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to save expenses');
    }

    const data = await response.json();
    
    // If save was successful and we have new items without IDs, update IDs without reordering
    if (data.status) {
      await updateItemIdsWithoutReordering();
    }

    return data;
  } catch (error) {
    console.error("Error saving expenses:", error);
    throw error;
  } finally {
    isSaving.value = false;
  }
};

// Event handlers
const handleDeleteItem = async (categoryIndex, itemIndex) => {
  const item = projectCategories.value[activeProjectIndex.value][categoryIndex].items[itemIndex];
  
  if (item.id) {
    conformationPopupIsOpen.value = true;
    deletionFunction.value = async () => {
      const success = await deleteExpense(item.id);
      if (success) {
        projectCategories.value[activeProjectIndex.value][categoryIndex].items.splice(itemIndex, 1);
      } else {
        console.error('Failed to delete expense');
      }
    };
  } else {
    projectCategories.value[activeProjectIndex.value][categoryIndex].items.splice(itemIndex, 1);
  }
};

// Handle data changes from BudgetTable for auto-save
const handleDataChanged = async (immediate = false) => {
  try {
    // Don't trigger auto-save if we're already saving
    if (isSaving.value) {
      return;
    }
    
    // Clear any existing validation error when user makes changes
    if (validationError.value) {
      validationError.value = '';
    }
    
    // Clear existing timer
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }
    
    if (immediate) {
      // Save immediately
      try {
        await saveExpenses();
      } catch (error) {
        console.error('Immediate save failed:', error);
      }
    } else {
      // Set new timer for debounced save
      autoSaveTimer = setTimeout(async () => {
        try {
          // Trigger auto-save
          await saveExpenses();
        } catch (error) {
          console.error('Auto-save failed:', error);
          // Don't show error to user for auto-save failures to avoid interrupting their workflow
          // The error will be shown when they try to validate the step
        }
      }, AUTO_SAVE_DELAY);
    }
  } catch (error) {
    console.error('Error setting up auto-save:', error);
  }
};

// Update item IDs without changing order
const updateItemIds = (savedExpenses) => {
  // Create a map of expense descriptions to IDs for quick lookup
  const expenseMap = {};
  Object.values(savedExpenses).flat().forEach(expense => {
    const key = `${expense.expense_type}|${expense.expense_description}`;
    expenseMap[key] = expense.id;
  });
  
  // Update IDs in project categories without changing order
  projectCategories.value.forEach((projectCategories, projectIndex) => {
    projectCategories.forEach(category => {
      category.items.forEach(item => {
        if (!item.id) { // Only update items that don't have an ID yet
          const key = `${category.name}|${item.name}`;
          if (expenseMap[key]) {
            item.id = expenseMap[key];
          }
        }
      });
    });
  });
};

// Add method to update item IDs without reordering
const updateItemIdsWithoutReordering = async () => {
  try {
    // Check if we have any items without IDs
    const hasNewItems = projectCategories.value.some(projectCategories => 
      projectCategories.some(category => 
        category.items.some(item => !item.id && item.name && item.name.trim() !== '')
      )
    );
    
    if (!hasNewItems) {
      return; // No new items to update
    }
    
    // Fetch the latest expenses for all projects to get new IDs
    await Promise.all(projects.value.map(async (project, projectIndex) => {
      const response = await fetch(`${global.API_URL}/expenses?project_id=${project.id}`, {
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      
      // Create a map of expense descriptions to IDs for quick lookup
      const expenseMap = {};
      Object.values(data.expenses).flat().forEach(expense => {
        const key = `${expense.expense_type}|${expense.expense_description}`;
        expenseMap[key] = expense.id;
      });
      
      // Update IDs in categories without changing order
      let updatedCount = 0;
      projectCategories.value[projectIndex].forEach(category => {
        category.items.forEach(item => {
          if (!item.id && item.name && item.name.trim() !== '') { // Only update items that don't have an ID yet
            const key = `${category.name}|${item.name.trim()}`;
            if (expenseMap[key]) {
              item.id = expenseMap[key];
              updatedCount++;
            }
          }
        });
      });
      
      // Only update total budget if we actually updated some items
      if (updatedCount > 0 && data.expenses && data.expenses.programs && data.expenses.programs.length > 0) {
        total_projects_budget.value = Number(data.expenses.programs[0].expense_cost);
      }
    }));
  } catch (error) {
    console.error('Error updating item IDs:', error);
    // Don't throw error to avoid breaking the save flow
  }
};

// Custom validation function for the useStepValidation composable
const validateStep = async () => {
  try {
    // Check if there are any projects
    if (projects.value.length === 0) {
      validationError.value = "Please add projects in the New Projects section first.";
      await nextTick();
      scrollToError();
      return false;
    }

    // First check if the current project has expenses
    const currentProjectCategories = projectCategories.value[activeProjectIndex.value];
    const currentProjectHasExpenses = currentProjectCategories.some(category => category.items.length > 0);
    
    if (!currentProjectHasExpenses) {
      // Current project is incomplete, stay on this tab and show error
      validationError.value = `Please add at least one expense item for ${projects.value[activeProjectIndex.value].project_name || `Project ${activeProjectIndex.value + 1}`} before proceeding.`;
      await nextTick();
      scrollToError();
      return false;
    }

    // Check if this is the last project tab
    const isLastProject = activeProjectIndex.value === projects.value.length - 1;
    
    if (isLastProject) {
      // Only check all projects when on the last project tab
      const incompleteProjects = projectCategories.value.map((projectCategories, index) => ({
        index,
        hasExpenses: projectCategories.some(category => category.items.length > 0)
      })).filter(project => !project.hasExpenses);

      if (incompleteProjects.length > 0) {
        // Show popup for the first incomplete project
        const firstIncompleteProject = incompleteProjects[0];
        incompleteProjectIndex.value = firstIncompleteProject.index;
        incompleteProjectMessage.value = `You haven't completed the expense table for ${projects.value[firstIncompleteProject.index].project_name || `Project ${firstIncompleteProject.index + 1}`}. Please go to this project tab and complete it before proceeding.`;
        incompleteProjectPopupIsOpen.value = true;
        return false;
      }
      
      // All projects are complete, try to save the expenses
      try {
        await saveExpenses();
        return true;
      } catch (error) {
        console.error('Error saving expenses:', error);
        validationError.value = "Failed to save expenses. Please try again.";
        await nextTick();
        scrollToError();
        return false;
      }
    } else {
      // Not the last project, just save current project's expenses and allow proceeding
      try {
        await saveExpenses();
        return true;
      } catch (error) {
        console.error('Error saving expenses:', error);
        validationError.value = "Failed to save expenses. Please try again.";
        await nextTick();
        scrollToError();
        return false;
      }
    }
  } catch (error) {
    console.error('Error validating step:', error);
    validationError.value = error.message || 'Failed to validate data. Please try again.';
    await nextTick();
    scrollToError();
    return false;
  }
};

// Use the validation composable with custom validation function
useStepValidation({
  initialValidState: false,
  validateFn: validateStep
});

// Expose necessary properties and methods for parent component
defineExpose({
  saveExpenses,
  handleDeleteItem,
  activeTab,
  tabs,
  validateCurrentTab
});
</script>

<style scoped>
</style> 